import React, { useState } from 'react';
import { updateUser } from '../../api/admin';
import ManageRolesModal from './ManageRolesModal';
import ResetPasswordModal from './ResetPasswordModal';
import { getCountryFlag } from '../../util/storage';

export default function AdminUserDetail({ user, onClose, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    email: user.email || '',
    phone_number: user.phone_number || '',
    address: user.address || '',
    country_code: user.country_code || '',
    region: user.region || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Build updates object with only changed fields
      const updates = {};
      if (formData.full_name !== user.full_name) updates.full_name = formData.full_name;
      if (formData.email !== user.email) updates.email = formData.email;
      if (formData.phone_number !== (user.phone_number || '')) updates.phone_number = formData.phone_number || null;
      if (formData.address !== (user.address || '')) updates.address = formData.address || null;
      if (formData.country_code !== (user.country_code || '')) updates.country_code = formData.country_code || null;
      if (formData.region !== user.region) updates.region = formData.region;

      if (Object.keys(updates).length === 0) {
        setEditing(false);
        return;
      }

      await updateUser(user.id, updates);
      setEditing(false);
      onUpdate(); // Refresh parent
    } catch (err) {
      console.error('Failed to update user:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: user.full_name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      address: user.address || '',
      country_code: user.country_code || '',
      region: user.region || '',
    });
    setEditing(false);
    setError('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      <div className="overlay" onClick={onClose}></div>
      <div className="right-sheet">
        <div className="sheet-header">
          <h3>User Details</h3>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="sheet-body">
          {error && <div className="error">{error}</div>}

          {editing ? (
            <form className="user-form">
              <div className="form-group">
                <label>Username</label>
                <input type="text" value={user.username} disabled />
                <small>Username cannot be changed</small>
              </div>

              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Country Code</label>
                <div className="country-input-wrapper">
                  <span className="country-input-flag">{getCountryFlag(formData.country_code)}</span>
                  <input
                    type="text"
                    value={formData.country_code}
                    onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })}
                    maxLength={2}
                    placeholder="US, CA, GB..."
                    className="country-input"
                  />
                </div>
                <small>2-letter country code (optional)</small>
              </div>

              <div className="form-group">
                <label>Region *</label>
                <select
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  required
                >
                  <option value="global">Global</option>
                  <option value="north-america">North America</option>
                  <option value="europe">Europe</option>
                  <option value="asia-pacific">Asia Pacific</option>
                </select>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="user-details-view">
              <div className="detail-row">
                <label>ID</label>
                <span className="mono">{user.id}</span>
              </div>

              <div className="detail-row">
                <label>Username</label>
                <span>{user.username}</span>
              </div>

              <div className="detail-row">
                <label>Full Name</label>
                <span>{user.full_name}</span>
              </div>

              <div className="detail-row">
                <label>Email</label>
                <span>{user.email}</span>
              </div>

              {user.phone_number && (
                <div className="detail-row">
                  <label>Phone</label>
                  <span>{user.phone_number}</span>
                </div>
              )}

              {user.address && (
                <div className="detail-row">
                  <label>Address</label>
                  <span>{user.address}</span>
                </div>
              )}

              {user.country_code && (
                <div className="detail-row">
                  <label>Country</label>
                  <span className="country-display">
                    <span className="country-flag-detail">{getCountryFlag(user.country_code)}</span>
                    <span className="country-code">{user.country_code}</span>
                  </span>
                </div>
              )}

              <div className="detail-row">
                <label>Region</label>
                <span>{user.region}</span>
              </div>

              <div className="detail-row">
                <label>Roles</label>
                <div className="role-badges">
                  {user.roles.map((role) => (
                    <span key={role} className={`badge role-${role}`}>
                      {role}
                    </span>
                  ))}
                </div>
              </div>

              <div className="detail-row">
                <label>Status</label>
                <span className={`badge status-${user.active ? 'active' : 'inactive'}`}>
                  {user.active ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="detail-row">
                <label>Created</label>
                <span>{formatDate(user.created_at)}</span>
              </div>

              <div className="detail-row">
                <label>Last Updated</label>
                <span>{formatDate(user.updated_at)}</span>
              </div>

              <div className="detail-actions">
                <button className="btn-primary" onClick={() => setEditing(true)}>
                  Edit Profile
                </button>
                <button className="btn-secondary" onClick={() => setShowRolesModal(true)}>
                  Manage Roles
                </button>
                <button className="btn-secondary" onClick={() => setShowPasswordModal(true)}>
                  Reset Password
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showRolesModal && (
        <ManageRolesModal
          user={user}
          onClose={() => setShowRolesModal(false)}
          onUpdate={onUpdate}
        />
      )}

      {showPasswordModal && (
        <ResetPasswordModal
          user={user}
          onClose={() => setShowPasswordModal(false)}
        />
      )}
    </>
  );
}
